import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Lock, Crown, Building2, ArrowRight } from 'lucide-react';

const PLAN_META = {
  pro: { name: 'Professional', price: 49, icon: Crown },
  enterprise: { name: 'Enterprise', price: 199, icon: Building2 },
};

export function UpgradeGate({ feature, children, title, description }) {
  const { canAccess, loading, plan, features } = useSubscription();
  const navigate = useNavigate();

  if (loading) return children;
  if (canAccess(feature)) return children;

  const featureInfo = features[feature] || {};
  const requiredPlan = featureInfo.required_plan || 'pro';
  const meta = PLAN_META[requiredPlan] || PLAN_META.pro;
  const PlanIcon = meta.icon;

  return (
    <div className="min-h-[400px] flex items-center justify-center p-6" data-testid={`upgrade-gate-${feature}`}>
      <Card className="max-w-md w-full bg-white border border-slate-200 shadow-sm text-navy-900 overflow-hidden">
        <div className="h-1.5 bg-coral-500" />
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-coral-100 flex items-center justify-center mx-auto mb-5">
            <Lock className="w-7 h-7 text-coral-600" />
          </div>

          <h2 className="text-xl font-bold mb-2 text-navy-900" data-testid="upgrade-gate-title">
            {title || 'Premium Feature'}
          </h2>
          <p className="text-slate-600 text-sm mb-6 leading-relaxed">
            {description || `This feature requires the ${meta.name} plan. Upgrade to use this feature.`}
          </p>

          <div className="bg-cream-100 rounded-xl p-4 mb-6 border border-slate-200">
            <div className="flex items-center justify-center gap-3">
              <PlanIcon className="w-5 h-5 text-coral-600" />
              <span className="text-navy-900 font-semibold">{meta.name}</span>
              <span className="text-slate-300">|</span>
              <span className="text-navy-900 font-bold text-lg">${meta.price}</span>
              <span className="text-slate-500 text-sm">/mo</span>
            </div>
          </div>

          <Button
            onClick={() => navigate('/pricing')}
            className="w-full bg-coral-500 hover:bg-coral-600 text-white font-medium"
            data-testid="upgrade-gate-btn"
          >
            Upgrade Now <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          <p className="text-slate-500 text-xs mt-4">
            Currently on: <span className="text-navy-900 font-medium capitalize">{plan}</span> plan
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
